import { ReactElement } from 'react';
import axios, { isAxiosError } from 'axios';
import { t } from 'i18next';

import {
  translateAdoptionEmailForTransport,
  translateAdoptionEmailFromTransport,
  translateAdoptionEmailTemplateForTransport,
  translateAdoptionEmailTemplateFromTransport,
} from '@/models/AdoptionEmail';
import { translateThreadForTransport, translateThreadFromTransport } from '@/models/Thread';
import {
  translateUnsavedPromptForTransport,
  translateUnsavedPromptFromTransport,
} from '@/models/UnsavedPrompt';
import { capitalize } from '@/utils/capitalize';
import i18n from '../i18n';
import {
  Book,
  Company,
  Connection,
  ConnectionType,
  Content,
  Person,
  Product,
  Question,
  translateAdoptionForTransport,
  translateAdoptionFromTransport,
  translateAssignmentForTransport,
  translateAssignmentFromTransport,
  translateBookForTransport,
  translateBookFromTransport,
  translateBulkAssignmentForTransport,
  translateBulkAssignmentFromTransport,
  translateCitationForTransport,
  translateCitationFromTransport,
  translateContentForTransport,
  translateContentFromTransport,
  translateCoursewareForTransport,
  translateCoursewareFromTransport,
  translateLaunchForTransport,
  translateLaunchFromTransport,
  translateNoteForTransport,
  translateNoteFromTransport,
  translatePollQuestionForTransport,
  translatePollQuestionFromTransport,
  translatePriceForTransport,
  translatePriceFromTransport,
  translateProductForTransport,
  translateProductFromTransport,
  translateQuestionForTransport,
  translateQuestionFromTransport,
  translateTransactionForTransport,
  translateTransactionFromTransport,
} from '../models';
import {
  translateAccessCodeForTransport,
  translateAccessCodeFromTransport,
} from '../models/AccessCode';
import {
  Edition,
  translateEditionCloneForTransport,
  translateEditionCloneFromTransport,
  translateEditionForTransport,
  translateEditionFromTransport,
} from '../models/Edition';
import {
  translateFlashcardForTransport,
  translateFlashcardFromTransport,
} from '../models/Flashcard';
import { translateIssueForTransport, translateIssueFromTransport } from '../models/Issue';
import { Item } from '../models/Item';
import { ItemType } from '../models/ItemType';
import { ItemsAndCount } from '../models/ItemsAndCount';
import { translateMessageForTransport, translateMessageFromTransport } from '../models/Message';
import { Print } from '../models/Print';
import {
  translateSummarizationForTransport,
  translateSummarizationFromTransport,
} from '../models/Summarization';
import {
  translateTranslationForTransport,
  translateTranslationFromTransport,
} from '../models/Translation';
import { myLocalStorage } from '../myLocalStorage';
import { Logger } from '../utils/logger';
import ImageWithFallback from '../views/ImageFallback';
import ImageIconWithFallback from '../views/ImageIconFallback';
import { Direction } from '../views/Layout/Direction';
import { unwrap } from './utils';

export interface CreateItemResponse {
  isSuccess?: boolean;
  message?: string;
  result?: Item;
}

export interface UpdateItemResponse {
  isSuccess?: boolean;
  message?: string;
  result?: Item;
}

export interface ItemAndCacheResponse {
  isSuccess?: boolean;
  message?: string;
  result?: Item;
}

export type GetItemByIdResponse = ItemAndCacheResponse;

export interface DeleteItemByIdResponse {
  isSuccess?: boolean;
  message?: string;
}

export interface DownloadResponse {
  isSuccess?: boolean;
  message?: string;
  result?: string; // url
}

export interface ItemsAndCountResponse {
  isSuccess?: boolean;
  message?: string;
  result?: ItemsAndCount;
}

export type GetItemsResponse = ItemsAndCountResponse;

export interface NetworkResponse<T = any> {
  isSuccess?: boolean;
  message?: string;
  result?: T;
}

export enum NetworkProtocol {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
}

export const getImageForEdition = (edition: Edition, dimension: number = 40): ReactElement => {
  const imageUrl = getImageUrlForEdition(edition);
  const editionNumberName = edition?.number ? String(edition.number) : '';

  return (
    <ImageIconWithFallback
      alt={editionNumberName}
      name={editionNumberName}
      src={imageUrl}
      dimension={dimension}
      width={dimension}
    />
  );
};

export const getImageForBook = (book: Book, dimension: number = 40) => {
  const imageUrl = getImageUrlForBook(book);
  return (
    <ImageIconWithFallback
      alt={book.title}
      id={`book${book?.id}`}
      name={book.title}
      src={imageUrl}
      dimension={dimension}
      width={dimension}
    />
  );
};

export const getImageForContent = (content: Content) => {
  const imageUrl = getImageUrlForContent(content);

  return (
    <img
      id={`content-${content?.id}`}
      alt={content?.name ?? ''}
      src={`${imageUrl}?d=${new Date().getTime()}`}
      height={125}
      style={{
        overflow: 'hidden',
      }}
    />
  );
};

export const getImageForProduct = (product?: Product, dimension: number = 250) => {
  const imageUrl = getImageUrlForProduct(product);
  return (
    <ImageIconWithFallback
      id={`product${product?.id ?? new Date().getTime()}`}
      name={product?.bookTitle ?? ''}
      src={imageUrl}
      alt={product?.bookTitle ?? ''}
      dimension={dimension}
      width={dimension}
    />
  );
};

export const defaultCompanyImageUrl = (): string => {
  return 'images/companyPlaceholder.jpg';
};

export const defaultContentImageUrl = (): string => {
  return 'images/defaultBook.png';
};

export const defaultProductImageUrl = (): string => {
  return 'images/defaultBook.png';
};

export const defaultPersonImageUrl = (): string => {
  return 'images/default.png';
};

export const getImageForPerson = (
  url: string | undefined,
  altText: string,
  dimension: number = 40,
  isDecorative: boolean = true
) => {
  const src = url?.includes('http') ? url : getImageUrl(url ?? defaultPersonImageUrl());

  return (
    <ImageWithFallback
      name={altText}
      alt={altText}
      src={src}
      dimension={dimension}
      width={dimension}
      height={dimension}
      isRound
      isDecorative={isDecorative}
      {...(!isDecorative
        ? {
            id: `person-${String(altText).replace(/[^\w-]+/g, '-')}`,
          }
        : {})}
    />
  );
};

export const getImageForCompany = (company: Company, dimension = 40, alt?: string) => {
  let imageUrl = getImageUrlForCompany(company);
  imageUrl = `${imageUrl}?d=${new Date().getTime()}`;
  return (
    <ImageWithFallback
      id={`company${company?.id ?? new Date().getTime()}`}
      name={company?.name ?? 'No Company Name'}
      src={imageUrl}
      alt={alt ?? company?.name ?? 'Company Image'}
      dimension={dimension}
    />
  );
};

export const getImageForConnectionType = (type: ConnectionType, alt: string, size?: number) => {
  const imageUrl = getImageUrlForConnectionType(type);
  const dimension = `${size ?? 40}px`;
  return <img alt={alt} src={imageUrl} width={dimension} height={dimension} />;
};

export const parameterizeFilters = (filters: any): string => {
  if (!filters) return '';
  const entries = Object.entries(filters).filter(
    ([_, value]) => value !== undefined && value !== null && value !== ''
  );
  return entries
    .map(([key, value]) =>
      key === 'includeImprints' ? `${key}=${value}` : `filter${capitalize(key)}=${value}`
    )
    .join('&');
};

export enum LoadingState {
  Loading = 'Loading',
  Complete = 'Complete',
  Ready = 'Ready',
}

export enum DownloadingState {
  Downloading = 'Downloading',
  Complete = 'Complete',
  Ready = 'Ready',
}

export const downloadItems = (
  type: ItemType,
  callback: (response: NetworkResponse) => void,
  limit: number = 10,
  offset: number = 0,
  filters: any = {},
  includes: string[] = [],
  direction: Direction = Direction.ASC,
  order: string = 'id'
): void => {
  const filterList = parameterizeFilters(filters);
  const includeList = parameterizeIncludes(includes);
  const url = `${type}?download=true&limit=${limit}&offset=${offset}&direction=${direction}&order=${order}${filterList ? `&${filterList}` : ``}${includeList ? `&${includeList}` : ``}`;
  Logger.log(`Requesting.. ${url}`);
  netGet(url, callback);
};

export const getItems = (
  type: ItemType,
  callback: (response: NetworkResponse) => void,
  limit: number = 10,
  offset: number = 0,
  filters: any = {},
  includes: string[] = [],
  direction: Direction = Direction.ASC,
  order: string = 'id'
): void => {
  const filterList = parameterizeFilters(filters);
  const includeList = parameterizeIncludes(includes);
  const url = `${type}?limit=${limit}&offset=${offset}&direction=${direction}&order=${order}${filterList ? `&${filterList}` : ``}${includeList ? `&${includeList}` : ``}`;
  netGet(url, (response: NetworkResponse) =>
    translateCallbackItems(type, response).then((response: NetworkResponse) => callback(response))
  );
};

export interface GetItemsAsyncArgs {
  type: ItemType;
  limit?: number;
  offset?: number;
  filters?: Record<string, unknown>;
  includes?: string[];
  direction?: Direction;
  order?: string;
}

export const getItemsAsync = async <T,>(
  {
    type,
    limit = 10,
    offset = 0,
    filters = {},
    includes = [],
    direction = Direction.ASC,
    order = 'id',
  }: GetItemsAsyncArgs,
  signal: AbortSignal
) => {
  const filterList = parameterizeFilters(filters);
  const includeList = parameterizeIncludes(includes);
  const url = `${type}?limit=${limit}&offset=${offset}&direction=${direction}&order=${order}${filterList ? `&${filterList}` : ``}${includeList ? `&${includeList}` : ``}`;
  const response = await netGet(url, undefined, signal).then((res) =>
    translateCallbackItems(type, res)
  );
  return unwrap<T>(response);
};

export const deleteItemById = (
  type: ItemType,
  id: number | string,
  callback: (response: NetworkResponse) => void
): void => {
  const url = `${type}/${id}`;
  Logger.log(`Deleting.. ${url}`);
  netDelete(url, callback);
};

interface DeleteItemByIdArgs {
  type: ItemType;
  id?: number | string;
}

export const deleteItemByIdAsync = async <TResponse = unknown,>({
  type,
  id,
}: DeleteItemByIdArgs) => {
  const response = await netDelete(`${type}/${id}`, undefined);
  return unwrap<TResponse>(response);
};

export const createItem = (
  type: ItemType,
  attributes: any,
  callback: (response: NetworkResponse) => void
): void => {
  const url = `${type}`;

  Logger.log(`Creating.. ${url} ${JSON.stringify(attributes)}`);

  // We often need to base64 items.
  const attributesForTransport = translateForTransport(type, attributes);

  netPost(url, callback, attributesForTransport);
};

export interface CreateItemAsyncArgs<T> {
  type: ItemType;
  attributes: T;
}

export const createItemAsync = async <TPayload, TResponse = unknown>({
  type,
  attributes,
}: CreateItemAsyncArgs<TPayload>) => {
  const response = await netPost(type, undefined, translateForTransport(type, attributes));
  return unwrap<TResponse>(response);
};

export interface UpdateItemAsyncArgs<T> {
  type: ItemType;
  attributes: T;
}

export const updateItemAsync = async <TPayload extends Item, TResponse = unknown>({
  type,
  attributes,
}: UpdateItemAsyncArgs<TPayload>) => {
  const url = `${type}/${attributes.id}`;
  const attributesForTransport = translateForTransport(type, attributes);

  const response = await netPut(url, undefined, attributesForTransport);
  return unwrap<TResponse>(response);
};

export const translateFromTransport = (type: ItemType, attributes: any): any => {
  switch (type) {
    case ItemType.Courseware:
      return translateCoursewareFromTransport(attributes);

    case ItemType.EditionClone:
      return translateEditionCloneFromTransport(attributes);

    case ItemType.Message:
      return translateMessageFromTransport(attributes);

    case ItemType.Summarization:
      return translateSummarizationFromTransport(attributes);

    case ItemType.Translation:
      return translateTranslationFromTransport(attributes);

    case ItemType.Edition:
      return translateEditionFromTransport(attributes);

    case ItemType.Transaction:
      return translateTransactionFromTransport(attributes);

    case ItemType.BulkCreateAssignment:
      return translateBulkAssignmentFromTransport(attributes);

    case ItemType.PollQuestion:
      return translatePollQuestionFromTransport(attributes);

    case ItemType.Question:
      return translateQuestionFromTransport(attributes);

    case ItemType.AdoptionEmail:
      return translateAdoptionEmailFromTransport(attributes);

    case ItemType.AdoptionEmailTemplate:
      return translateAdoptionEmailTemplateFromTransport(attributes);

    case ItemType.Assignment:
      return translateAssignmentFromTransport(attributes);

    case ItemType.Launch:
      return translateLaunchFromTransport(attributes);

    case ItemType.Citation:
      return translateCitationFromTransport(attributes);

    case ItemType.Note:
      return translateNoteFromTransport(attributes);

    case ItemType.AccessCode:
      return translateAccessCodeFromTransport(attributes);

    case ItemType.UnsavedPrompt:
      return translateUnsavedPromptFromTransport(attributes);

    case ItemType.Product:
      return translateProductFromTransport(attributes);

    case ItemType.Price:
      return translatePriceFromTransport(attributes);

    case ItemType.Issue:
      return translateIssueFromTransport(attributes);

    case ItemType.Flashcard:
      return translateFlashcardFromTransport(attributes);

    case ItemType.Adoption:
      return translateAdoptionFromTransport(attributes);

    case ItemType.Book:
      return translateBookFromTransport(attributes);

    case ItemType.Content:
      return translateContentFromTransport(attributes);

    case ItemType.Thread:
      return translateThreadFromTransport(attributes);

    default:
      return attributes;
  }
};

export const translateForTransport = (type: ItemType, attributes: any): any => {
  switch (type) {
    case ItemType.Courseware:
      return translateCoursewareForTransport(attributes);

    case ItemType.EditionClone:
      return translateEditionCloneForTransport(attributes);

    case ItemType.Message:
      return translateMessageForTransport(attributes);

    case ItemType.Translation:
      return translateTranslationForTransport(attributes);

    case ItemType.QuestionUpload:
      return {
        questions: attributes?.questions?.map((question: Question) =>
          translateQuestionForTransport(question)
        ),
      };

    case ItemType.Transaction:
      return translateTransactionForTransport(attributes);

    case ItemType.Summarization:
      return translateSummarizationForTransport(attributes);

    case ItemType.AdoptionEmail:
      return translateAdoptionEmailForTransport(attributes);

    case ItemType.AdoptionEmailTemplate:
      return translateAdoptionEmailTemplateForTransport(attributes);

    case ItemType.Edition:
      return translateEditionForTransport(attributes);

    case ItemType.Book:
      return translateBookForTransport(attributes);

    case ItemType.Content:
      return translateContentForTransport(attributes);

    case ItemType.Citation:
      return translateCitationForTransport(attributes);

    case ItemType.Note:
      return translateNoteForTransport(attributes);

    case ItemType.AccessCode:
      return translateAccessCodeForTransport(attributes);

    case ItemType.PromptPreview:
    case ItemType.UnsavedPrompt:
      return translateUnsavedPromptForTransport(attributes);

    case ItemType.Product:
      return translateProductForTransport(attributes);

    case ItemType.Launch:
      return translateLaunchForTransport(attributes);

    case ItemType.Issue:
      return translateIssueForTransport(attributes);

    case ItemType.Price:
      return translatePriceForTransport(attributes);

    case ItemType.Assignment:
      return translateAssignmentForTransport(attributes);

    case ItemType.Adoption:
      return translateAdoptionForTransport(attributes);

    case ItemType.BulkCreateAssignment:
      return translateBulkAssignmentForTransport(attributes);

    case ItemType.PollQuestion:
      return translatePollQuestionForTransport(attributes);

    case ItemType.Question:
      return translateQuestionForTransport(attributes);

    case ItemType.Flashcard:
      return translateFlashcardForTransport(attributes);

    case ItemType.Thread:
      return translateThreadForTransport(attributes);

    default:
      return attributes;
  }
};

export const updateItem = (
  type: ItemType,
  attributes: any,
  callback: (response: NetworkResponse) => void
): void => {
  const url = `${type}/${attributes.id}`;

  const attributesForTransport = translateForTransport(type, attributes);

  netPut(url, callback, attributesForTransport);
};

const translateCallbackItems = async (
  type: ItemType,
  response: GetItemsResponse
): Promise<NetworkResponse> => {
  const { isSuccess, result } = response;

  if (isSuccess && result) {
    const { items, count } = result;
    if (items) {
      const newItems = [];
      for (const item of items) {
        newItems.push(translateFromTransport(type, item));
      }

      return { ...response, result: { ...result, count, items: newItems } };
    }
  }

  //Logger.log(`translateCallbackItems: Complete fallthrough`);
  return response;
};

const translateCallbackById = async (
  type: ItemType,
  response: GetItemByIdResponse
): Promise<NetworkResponse> => {
  //Logger.log(`translateCallback: ${type}: ${JSON.stringify(response)}`);
  const { isSuccess, message, result } = response;
  if (isSuccess) {
    return { isSuccess, message, result: translateFromTransport(type, result) };
  }

  return response;
};

export const getItemById = (
  type: ItemType,
  id: number | string,
  includes: string[],
  callback: (response: NetworkResponse) => void,
  filters?: any
): void => {
  const includeQueryParams = parameterizeIncludes(includes);
  const filterList = filters ? parameterizeFilters(filters) : undefined;
  const url = `${type}/${id}${includeQueryParams ? `?${includeQueryParams}` : ``}${filterList ? `&${filterList}` : ``}`;
  //Logger.log(`Requesting.. ${url}`);
  netGet(url, (response: NetworkResponse) =>
    translateCallbackById(type, response).then((response) => callback(response))
  );
};

export interface GetItemByIdAsyncArgs {
  type: ItemType;
  id: number | string;
  includes?: string[];
  filters?: Record<string, unknown>;
}

export const getItemByIdAsync = async <T,>(
  { type, id, includes = [], filters }: GetItemByIdAsyncArgs,
  signal: AbortSignal
) => {
  const includeQueryParams = parameterizeIncludes(includes);
  const filterList = filters ? parameterizeFilters(filters) : undefined;
  const url = `${type}/${id}${includeQueryParams ? `?${includeQueryParams}` : ``}${filterList ? `&${filterList}` : ``}`;

  const response = await netGet(url, undefined, signal).then((res) =>
    translateCallbackById(type, res)
  );
  return unwrap<T>(response);
};

export const testConnection = (
  connection: Connection,
  callback: (response: NetworkResponse) => void
): void => {
  const url = `connections/test`;
  netPost(url, callback, connection);
};

export const parameterizeIncludes = (includes: string[]): string => {
  let parameterizedIncludes = '';
  includes.forEach((include: string, index: number) => {
    parameterizedIncludes += `include${capitalize(include)}=true`;
    if (index < includes.length - 1) {
      parameterizedIncludes += '&';
    }
  });
  return parameterizedIncludes;
};

export const getImageUrlForEdition = (bookEdition: Edition): string => {
  //Logger.log(`bookEdition.coverImageUrl: ${bookEdition.coverImageUrl}`);
  return getImageUrl(
    bookEdition?.coverImageUrl && bookEdition.coverImageUrl !== ''
      ? bookEdition.coverImageUrl
      : defaultProductImageUrl()
  );
};

export const getImageUrlForBook = (book: Book): string => {
  return getImageUrl(book?.coverImageUrl ?? defaultProductImageUrl());
};

export const getUrlForPrint = (print: Print): string => {
  return getImageUrl(print?.path ?? '');
};

export const getImageUrlForProduct = (product?: Product): string => {
  return getImageUrl(
    product?.coverImageUrl && product.coverImageUrl !== ''
      ? product.coverImageUrl
      : defaultProductImageUrl()
  );
};

export const getImageUrlForContent = (content: Content): string => {
  return getImageUrl(content?.coverImageUrl ?? defaultContentImageUrl());
};

export const getImageUrlForPerson = (person?: Person): string => {
  if (person?.imageUrl || person?.imageUrl === '') {
    return getImageUrl(person?.imageUrl ?? defaultPersonImageUrl());
  } else {
    return defaultPersonImageUrl();
  }
};

export const getImageUrlForCompany = (company?: Company): string => {
  //Logger.log(`company.name: ${ company?.name }, company.imageUrl: ${company?.imageUrl}`);
  return getImageUrl(
    company?.imageUrl && company?.imageUrl !== '' ? company?.imageUrl : defaultCompanyImageUrl()
  );
};

export const getImageUrlForConnectionType = (type: ConnectionType): string => {
  const baseUrl = `https://cwai-public.s3.amazonaws.com/lms`;
  return `${baseUrl}/${type}.png`;
};

export const getSupplementalUrl = (path: string): string => {
  if (path.includes('http')) {
    return path;
  } else {
    const imageUrl = `${import.meta.env.VITE_PUBLIC_URL}/${path}`;
    //Logger.log(`imageUrl: ${imageUrl}`);
    return imageUrl;
  }
};

export const getImageUrl = (path: string): string => {
  //Logger.log(`getImageUrl: ${path}`);

  if (path.includes('http')) {
    return path;
  } else {
    const imageUrl = `${import.meta.env.VITE_PUBLIC_URL}/${path}`;
    //Logger.log(`imageUrl: ${imageUrl}`);
    return imageUrl;
  }
};

export const getContentSnapshotUrl = (path: string): string => {
  if (path.includes('http')) {
    return path;
  } else {
    const contentUrl = `${import.meta.env.VITE_PUBLIC_URL}/content/contents-screenshots/${path}`;
    //Logger.log(`imageUrl: ${contentUrl}`);
    return contentUrl;
  }
};

export const getContentUrl = (path: string): string => {
  if (path.includes('http')) {
    return path;
  }

  return `/content/${path}`;
};

const unprotectedPaths = [
  ItemType.CourseCode,
  ItemType.EmailConfirmation,
  ItemType.CodeConfirmation,
  ItemType.CourseCodeEnrollment,
  ItemType.CampusID,
  ItemType.PublisherPreview,
  ItemType.ValidatePubEmail,
  ItemType.CatalogPreview,
  ItemType.Impersonation,
];

const send = async (
  protocol: NetworkProtocol,
  path: string,
  callback: (response: NetworkResponse) => void,
  body?: any,
  signal?: AbortSignal
) => {
  if (!import.meta.env.VITE_SERVICE_URL) {
    callback({ isSuccess: false, message: i18n.t('serviceIsNotAvailable') });
    return;
  }

  const authToken = myLocalStorage.getItem(`authToken`);
  const isUnprotectedPath = unprotectedPaths.findIndex((up) => path.startsWith(up)) >= 0;

  if (!authToken && !isUnprotectedPath) {
    callback({ isSuccess: false, message: i18n.t('loginFirstAndTryAgain') });
    return;
  }

  const url = `${import.meta.env.VITE_SERVICE_URL}/v1/${path}`;

  if (!window.navigator.onLine) {
    callback({ isSuccess: false, message: i18n.t('youAppearToBeOffline') });
    return;
  }

  try {
    const headers: HeadersInit = {
      Authorization: `${authToken}`,
      'Content-Type': 'application/json',
      Accept: '*/*',
    };
    switch (protocol) {
      case NetworkProtocol.DELETE: {
        const res = await axios.delete(url, { headers });
        callback(res.data);
        break;
      }
      case NetworkProtocol.POST: {
        const res = await axios.post(url, body, { headers });
        callback(res.data);
        break;
      }
      case NetworkProtocol.PATCH:
      case NetworkProtocol.PUT: {
        const res = await axios.put(url, body, { headers });
        callback(res.data);
        break;
      }
      case NetworkProtocol.GET:
      default: {
        const res = await axios.get(url, { headers, signal });
        callback(res.data);
        break;
      }
    }
  } catch (error) {
    const message =
      error instanceof Error || isAxiosError(error)
        ? ((error as any)?.response?.data?.message ?? error.message)
        : t('unknownError');

    callback({ isSuccess: false, message });
  }
};

export const netGet = async (
  path: string,
  callback?: (response: NetworkResponse) => void,
  signal?: AbortSignal
): Promise<NetworkResponse> => {
  return new Promise<NetworkResponse>((resolve) => {
    send(
      NetworkProtocol.GET,
      path,
      (response) => {
        if (callback) {
          callback(response);
        }
        resolve(response);
      },
      undefined,
      signal
    );
  });
};

export const netPut = async (
  path: string,
  callback?: (response: NetworkResponse) => void,
  body?: any
): Promise<NetworkResponse> => {
  return new Promise<NetworkResponse>((resolve) => {
    send(
      NetworkProtocol.PUT,
      path,
      (response) => {
        if (callback) callback(response);
        resolve(response);
      },
      body
    );
  });
};

export const netPatch = async (
  path: string,
  callback: (response: NetworkResponse) => void,
  body: any
) => {
  return await send(NetworkProtocol.PATCH, path, callback, body);
};

export const netDelete = async (path: string, callback?: (response: NetworkResponse) => void) =>
  new Promise<NetworkResponse>((resolve) => {
    send(NetworkProtocol.DELETE, path, (response) => {
      if (callback) {
        callback(response);
      }
      resolve(response);
    });
  });

export const netPost = async (
  path: string,
  callback?: (response: NetworkResponse) => void,
  body?: any
) =>
  new Promise<NetworkResponse>((resolve) => {
    send(
      NetworkProtocol.POST,
      path,
      (response) => {
        if (callback) {
          callback(response);
        }
        resolve(response);
      },
      body
    );
  });
